import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GeoPiButton from "@/components/GeoPiButton";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StepBar } from "@/components/wizard/StepBar";
import NotificationBell from "@/components/NotificationBell";
import Step1Project from "@/components/wizard/Step1Project";
import Step2Areas from "@/components/wizard/Step2Areas";
import StepGeology from "@/components/wizard/StepGeology";
import Step4Sensors from "@/components/wizard/Step4Sensors";
import Step5Imagery from "@/components/wizard/Step5Imagery";
import Step6TargetReview from "@/components/wizard/Step6TargetReview";
import Step4Downloads from "@/components/wizard/Step4Downloads";
import { ArrowLeft } from "lucide-react";

export default function WizardWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState(1);

  const reload = useCallback(() => api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => {}), [id]);
  useEffect(() => { window.__sgexTouched = false; reload(); api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, [id, reload]);

  useEffect(() => {
    if (!project || window.__sgexTouched) return;
    const areas = project.areas || [];
    if (areas.some((a) => a.status === "complete")) setStep(7);
    else if (areas.filter((a) => a.status !== "error").length) setStep(4);
    else if (areas.length) setStep(2);
    window.__sgexTouched = true; // resolve the landing step once; never re-route on poll reloads
  }, [project]);

  if (!project) return <div className="min-h-screen grid place-items-center bg-background text-muted-foreground">Loading project…</div>;

  const areas = project.areas || [];
  const hasValidArea = areas.filter((a) => a.status !== "error").length > 0;
  const anyComplete = areas.some((a) => a.status === "complete");
  const maxStep = anyComplete ? 7 : hasValidArea ? 6 : areas.length ? 3 : 2;

  const go = (n) => { window.__sgexTouched = true; setStep(n); };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-[52px] sticky top-0 z-[900] bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-3 px-4 sm:px-5">
        <button onClick={() => nav("/console")} data-testid="back-to-modes" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft size={16} /> <span className="hidden sm:inline">Console</span>
        </button>
        <button onClick={() => nav("/projects")} data-testid="wizard-back" className="text-sm text-muted-foreground hover:text-foreground border-l border-border pl-3 transition-colors">Projects</button>
        <div className="h-7 rounded-md bg-white border border-slate-200 grid place-items-center px-1.5 shrink-0 ml-1"><img src="/symin-logo.png" alt="SYMIN" className="h-full w-auto object-contain max-w-[88px]" /></div>
        <div className="min-w-0 leading-tight hidden md:block">
          <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Data &amp; Setup</div>
          <div className="font-display font-semibold text-sm truncate max-w-[240px]" data-testid="header-project-name">{project.name}</div>
        </div>
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <GeoPiButton compact project={{ id: project?.id, name: project?.name, context: `Module: Remote Sensing. Project: ${project?.name}. Commodity: ${(project?.commodities || []).join(", ") || project?.commodity || "n/a"}. Areas defined: ${(project?.areas || []).length}.` }} />
          {!areas.some((a) => ["queued", "processing", "complete"].includes(a.status)) && (
            <button onClick={() => nav(`/projects/${id}/advanced`)} data-testid="to-advanced"
              className="text-sm text-muted-foreground hover:text-foreground hidden sm:block transition-colors">Settings</button>
          )}
          <NotificationBell />
          <Button size="sm" className="h-8 font-semibold" onClick={() => nav(`/projects/${id}/command`)} data-testid="to-command-centre"
            style={{ background: "#C20511", color: "#FFFFFF" }}>
            Command Centre
          </Button>
          <div className="h-8 w-8 rounded-md bg-accent grid place-items-center text-xs font-bold" data-testid="header-user">
            {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
          </div>
        </div>
      </header>

      <StepBar step={step} maxStep={maxStep} onGo={go} pid={id} />

      <div className="max-w-6xl mx-auto px-6 py-8 md:py-12">
        {step === 1 && <Step1Project project={project} catalog={catalog} reload={reload} onNext={() => go(2)} />}
        {step === 2 && <Step2Areas project={project} catalog={catalog} reload={reload} onNext={() => go(3)} />}
        {step === 3 && <StepGeology project={project} reload={reload} onNext={() => go(4)} onBack={() => go(2)} />}
        {step === 4 && <Step4Sensors project={project} reload={reload} onNext={() => go(5)} onBack={() => go(3)} />}
        {step === 5 && <Step5Imagery project={project} reload={reload} onNext={() => go(6)} onBack={() => go(4)} />}
        {step === 6 && <Step6TargetReview project={project} reload={reload} onNext={() => go(7)} onBack={() => go(5)} />}
        {step === 7 && <Step4Downloads project={project} />}
      </div>
    </div>
  );
}
